import type { UseQueryResult } from '@tanstack/react-query';

import type { ErroApi } from '@/nucleo/auth/cliente-api';
import { paginar, type Pagina } from '@/modulos/portal-credenciado/comum/contrato';
import {
  documentosDoChecklist,
  textoOuNulo,
  useRecursosDoCredenciado,
  type RecursoDaApi,
} from '@/modulos/portal-credenciado/comum/recursos';

import type { FiltroDeMonitores, Monitor } from './contrato';

/**
 * Camada de dados dos monitores do credenciado: `comum/recursos.ts`, tipo
 * `monitor`. Nenhum componente busca dado direto.
 */

/** A linha da API no formato da tela. O que a API não devolve fica `null`. */
export function paraOMonitor(recurso: RecursoDaApi): Monitor {
  return {
    id: recurso.id,
    nome: textoOuNulo(recurso.nome) ?? '',
    cpf: recurso.identificador,
    situacao: recurso.estado,
    documentos: documentosDoChecklist(recurso.documentos),
    email: null,
    telefone: null,
    rg: null,
    data_nascimento: null,
    endereco: null,
    bairro: null,
    cep: null,
    vinculo_empregaticio: null,
    curso_primeiro_socorros: null,
    curso_transporte_escolar: null,
    observacoes: null,
  };
}

/**
 * Mesma busca do protótipo: pelo nome sempre, e também pelo CPF quando o termo
 * tem ao menos três dígitos (a pontuação digitada é ignorada). Ordem por nome.
 */
function filtrarMonitores(monitores: readonly Monitor[], filtro: FiltroDeMonitores): Monitor[] {
  const busca = filtro.busca?.trim().toLowerCase();
  const digitos = busca?.replace(/\D/g, '') ?? '';

  return monitores
    .filter((monitor) => {
      if (!busca) {
        return true;
      }
      if (monitor.nome.toLowerCase().includes(busca)) {
        return true;
      }
      return digitos.length >= 3 && monitor.cpf.includes(digitos);
    })
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
}

export function useMonitores(
  filtro: FiltroDeMonitores,
  pagina: number,
  tamanho: number,
): UseQueryResult<Pagina<Monitor>, ErroApi> {
  return useRecursosDoCredenciado('monitor', paraOMonitor, (monitores) =>
    paginar(filtrarMonitores(monitores, filtro), pagina, tamanho),
  );
}

/**
 * `null` quando o id não é de um monitor do cadastro: o React Query recusa
 * `undefined` como dado e trataria o "não encontrado" como falha de
 * carregamento.
 */
export function useMonitor(id: string | undefined): UseQueryResult<Monitor | null, ErroApi> {
  return useRecursosDoCredenciado(
    'monitor',
    paraOMonitor,
    (monitores) => monitores.find((monitor) => monitor.id === id) ?? null,
    !!id,
  );
}
